import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';

// Frame for the homepage 3D scenes. The whole three.js stack lives in ONE
// lazy chunk (./scenes) so the landing's first paint is unchanged; the chunk
// is only requested once the frame is near the viewport, and the canvas only
// renders frames while on screen. Static poster (the design mockup) shows
// until the scene reports its first frame — and permanently when the visitor
// prefers reduced motion, has no WebGL, or is on a small screen.
const Scenes = lazy(() => import('./scenes'));

function staticOnly() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
  if (window.matchMedia('(max-width: 640px)').matches) return true;
  try {
    const c = document.createElement('canvas');
    if (!(c.getContext('webgl2') || c.getContext('webgl'))) return true;
  } catch {
    return true;
  }
  return false;
}

// `poster` is an image URL shown while the scene loads (a real first frame of
// the scene, so the crossfade is invisible); `fallback` is a static React node
// used INSTEAD of the scene when we never render it (reduced motion, no WebGL,
// small screens). When both are given the poster covers loading and the
// fallback covers static — a poster is never shown on top of a different
// drawing.
export default function SceneFrame({ scene, poster, fallback, alt, aspect = '840 / 336', className = '' }) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false); // chunk requested + canvas created
  const [visible, setVisible] = useState(false); // currently on screen → render frames
  const [ready, setReady] = useState(false);     // first frame drawn → fade the poster
  // Decide static vs live before the first paint so the fallback drawing is
  // never flashed for a frame ahead of the poster.
  const [isStatic] = useState(() => staticOnly());

  useEffect(() => {
    if (isStatic || typeof IntersectionObserver === 'undefined' || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setMounted(true);
        setVisible(e.isIntersecting);
      });
    }, { rootMargin: '240px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [isStatic]);

  return (
    <div
      ref={ref}
      className={`cs-scene ${ready ? 'is-ready' : ''} ${className}`}
      style={{ aspectRatio: aspect }}
      data-scene={scene}
    >
      {fallback && (isStatic || !poster)
        ? <div className="cs-scene-poster">{fallback}</div>
        : <img className="cs-scene-poster" src={poster} alt={alt} loading="eager" decoding="async" />}
      {!isStatic && mounted && (
        <Suspense fallback={null}>
          <Scenes scene={scene} active={visible} onReady={() => setReady(true)} />
        </Suspense>
      )}
    </div>
  );
}
